export const normalizeAccessList = (accessList) => {
    if (Array.isArray(accessList)) {
        return accessList.reduce((result, key) => {
            result[key] = true;
            return result;
        }, {});
    }

    if (accessList && typeof accessList === "object") {
        return { ...accessList };
    }

    return {};
};

const POLICY_KEY_BY_LEGACY_KEY = Object.freeze({
    news_publishing: "news.publish",
    post_editing: "news.update",
    post_deleting: "news.delete",
    post_flagging: "news.flag",
    feed_data_download: "news.export",
    unreview_post: "news.review",
    page_search_space: "news.keyword_search",
    prospect_data_import: "prospects.import",
    prospect_data_download: "prospects.export",
    prospect_archiving: "prospects.archive",
    prospect_bulk_archive: "prospects.bulk_archive",
    tag_adding: "prospects.assign_tags",
    tag_importing: "prospects.assign_tags",
    tag_creating: "prospects.assign_tags",
    tag_updating: "prospects.assign_tags",
    merge_prospect: "prospects.merge",
    list_download: "directory.export",
    report_settings: "reports.rename",
    custom_filed_adding: "custom_fields.create",
    add_team_member: "members.invite",
    team_notification: "team.notifications.manage",
    team_setting: "team.settings.manage",
});

export const usesPolicyAuthorization = (team) =>
    team?.is_sso_enabled !== true && team?.policy_access_enabled === true;

export const resolveUserAccess = ({ actionType, user, team }) => {
    if (!actionType) return false;

    const effective = team?.effective_permissions;
    const policyKey = actionType === "news.keyword_search" || Object.prototype.hasOwnProperty.call(effective || {}, actionType)
        ? actionType
        : POLICY_KEY_BY_LEGACY_KEY[actionType];
    if (policyKey === "news.keyword_search" || actionType === "news.keyword_search") {
        if (!team || (!isStaffUser(user) && team.newsfeed_enabled === false)) return false;
        if (usesPolicyAuthorization(team) && effective?.["news.view"] !== true) return false;
    }
    if (usesPolicyAuthorization(team) && policyKey) {
        return effective?.[policyKey] === true;
    }

    return Boolean(
        user?.access_list?.[actionType] ||
        team?.access_list?.[actionType]
    );
};

const FEED_TYPE_PERMISSION = Object.freeze({
    articles: "news.view",
    Job_Updates: "jobs.view",
    SEC_Filings: "sec.view",
});

const FEED_TYPE_TEAM_SETTING = Object.freeze({
    articles: "newsfeed_enabled",
    Job_Updates: "feed_job_updates_enabled",
    SEC_Filings: "sec_filings_enabled",
});

export const canViewFeedType = ({ typeKey, team, user }) => {
    if (!team) return false;

    if (typeKey === "all") {
        return Object.keys(FEED_TYPE_PERMISSION).some((key) =>
            canViewFeedType({ typeKey: key, team, user })
        );
    }

    const permissionKey = FEED_TYPE_PERMISSION[typeKey];
    if (!permissionKey) return false;

    if (user?.is_super_member === true) {
        return team[FEED_TYPE_TEAM_SETTING[typeKey]] === true;
    }

    if (usesPolicyAuthorization(team)) {
        return team.effective_permissions?.[permissionKey] === true;
    }

    if (typeKey === "articles") return true;
    if (typeKey === "Job_Updates") return team.feed_job_updates_enabled === true;
    if (typeKey === "SEC_Filings") return team.sec_filings_enabled === true;
    return false;
};

export const canUsePolicyAction = ({ permissionKey, team, legacyAllowed = true }) => {
    if (!team || !permissionKey) return false;

    if (!usesPolicyAuthorization(team)) return legacyAllowed === true;

    return team.effective_permissions?.[permissionKey] === true;
};

export const canPublishFeedType = ({ postType, user, team }) => {
    const permissions = {
        Post: ["news.publish", "news.view", "newsfeed_enabled"],
        "Post::JobUpdates": ["jobs.publish", "jobs.view", "feed_job_updates_enabled"],
        "Post::SecFiling": ["sec.publish", "sec.view", "sec_filings_enabled"],
    }[postType];
    if (!team || !Array.isArray(permissions)) return false;

    const [publishKey, viewKey, featureKey] = permissions;
    if (!isStaffUser(user) && team[featureKey] === false) return false;
    if (usesPolicyAuthorization(team)) {
        return team.effective_permissions?.[publishKey] === true &&
            team.effective_permissions?.[viewKey] === true;
    }

    return resolveUserAccess({ actionType: "news_publishing", user, team });
};

const isStaffUser = (user) =>
    user?.is_super_member === true ||
    user?.is_publisher === true;

export const isAiReportsFeatureAvailable = ({ user, team }) => {
    if (!team) return false;
    if (isStaffUser(user)) return true;

    return team.displayable_features?.display_new_directory === true &&
        team.displayable_features?.display_ai_reports === true;
};

// "Show only mapped data" for regular users
export const isMappedOnlyFor = ({ user, team }) => Boolean(team?.displayable_features?.display_only_mapped_data && user?.is_super_member !== true);

export const canUseAiReports = ({ user, team }) => {
    if (isStaffUser(user)) return true;
    if (!isAiReportsFeatureAvailable({ user, team })) return false;

    return canUsePolicyAction({
        permissionKey: "reports.create_ai",
        team,
        legacyAllowed: true,
    });
};

export const isCustomerTeamAdmin = ({ team }) => {
    if (!team) return false;
    // Staff retain the legacy is_admin UI flag. The explicit capability is the
    // authority for People controls on both policy and SSO teams.
    if (typeof team.access_management_admin === "boolean") {
        return team.access_management_admin;
    }
    // Preserve compatibility with payloads from before this capability existed.
    if (usesPolicyAuthorization(team)) {
        return team.can_manage_access_roles === true;
    }
    return team.is_admin === true;
};

export const canManageExistingTags = ({ team, legacyAllowed = false }) => {
    if (!team) return false;
    if (!usesPolicyAuthorization(team)) return legacyAllowed === true;

    return isCustomerTeamAdmin({ team }) || (
        team.role === "custom" &&
        team.effective_permissions?.["prospects.assign_tags"] === true
    );
};

export const delegatedMemberRoleOptions = ({ team, customRoles = [] }) => {
    if (!usesPolicyAuthorization(team) || team.role !== "custom" || !team.access_role?.id) {
        return [];
    }

    const ownRole = customRoles.find((role) => role.id === team.access_role.id) || team.access_role;
    return [
        { label: ownRole.name, value: "custom:" + ownRole.id },
    ];
};

export const MEMBER_MANAGEMENT_PERMISSION_KEYS = Object.freeze([
    "members.invite",
    "members.approve",
    "members.remove",
]);

export const EXPORT_PERMISSION_KEYS = Object.freeze([
    "news.export",
    "prospects.export",
    "directory.export",
    "mapper.export",
    "reports.export",
]);

const EXPORT_SOURCE_PERMISSION_KEYS = Object.freeze({
    crm: Object.freeze(["directory.export", "reports.export"]),
    prospects_feed: Object.freeze(["news.export", "prospects.export", "mapper.export"]),
});

export const canUseAnyExport = ({ team, legacyAllowed = false }) => {
    if (!team) return false;
    if (!usesPolicyAuthorization(team)) return legacyAllowed === true;
    return EXPORT_PERMISSION_KEYS.some((key) => team.effective_permissions?.[key] === true);
};

export const canUseExportSource = ({ team, source, internalCrmEnabled }) => {
    if (!team || !EXPORT_SOURCE_PERMISSION_KEYS[source]) return false;
    if (!usesPolicyAuthorization(team)) return true;

    const permissionKeys = internalCrmEnabled === true
        ? EXPORT_SOURCE_PERMISSION_KEYS[source]
        : EXPORT_PERMISSION_KEYS;
    return permissionKeys.some((key) => team.effective_permissions?.[key] === true);
};

export const canUseMemberManagementAction = ({ permissionKey, user, team }) => {
    const customerTeamAdmin = isCustomerTeamAdmin({ team });
    if (user?.is_super_member === true && !customerTeamAdmin) return false;

    return canUsePolicyAction({
        permissionKey,
        team,
        legacyAllowed: customerTeamAdmin,
    });
};

export const canUseAnyMemberManagementAction = ({ user, team }) =>
    MEMBER_MANAGEMENT_PERMISSION_KEYS.some((permissionKey) =>
        canUseMemberManagementAction({ permissionKey, user, team })
    );
